import {
  Button,
  Icon,
  Label,
  Select,
  Tooltip,
  Badge,
  LegacyStack,
  TextField,
  FormLayout,
  TextContainer,
} from "@shopify/polaris";
import { XIcon } from "@shopify/polaris-icons";
import type { MutableRefObject} from "react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatMoney, assertNumber, getCurrencySymbol, extractMessageFromError, cloneArrayOfObjects } from "../../../helper_functions/utils";
import { saveCostAdjustments, useCostAdjustments } from "../../../api_utils/requests";
import type { CostAdjustment, CostAdjustmentEditable, PurchaseOrderState } from "../../../api_utils/types";

const COST_ITEMS = [
  "Custom duties",
  "Discount",
  "Foreign transaction fee",
  "Freight fee",
  "Insurance",
  "Rush fee",
  "Surcharge",
  "Shipping",
  "Other",
];

// Make discounts negative
const formatAmount = (costAdjustment: CostAdjustmentEditable): number => {
  const amountNumber = assertNumber(costAdjustment.amount);
  return costAdjustment.label === "Discount" ? -Math.abs(amountNumber) : amountNumber;
};

const formatClientSide = (costAdjustments: CostAdjustment[]): CostAdjustmentEditable[] =>
  costAdjustments.map((costAdjustment) => ({
    ...costAdjustment,
    amount: formatMoney(costAdjustment.amount),
  }));

const sortCostAdjustments = (costAdjustments: CostAdjustmentEditable[]): CostAdjustmentEditable[] =>
  costAdjustments.sort((a, b) => {
    // Sort based on ID, but always make the tax adjustment (label == "Tax") the first element
    if (a.label === "Tax") {
      return -1;
    } else if (b.label === "Tax") {
      return 1;
    } else  if (a.id < b.id) {
      return -1;
    } else if (a.id > b.id) {
      return 1;
    } else {
      return 0;
    }
  });

function CostAdjustments({
  currency,
  lineTotal = 0,
  receivedCost = 0,
  purchaseOrder,
  setErrorMessage,
  setIsDirty,
  saveCallback,
  discardCallback,
}: {
  readonly currency: string;
  readonly lineTotal: number;
  readonly receivedCost?: number;
  readonly purchaseOrder: { id: number; supplier: { minimum_order_value: number }; state: PurchaseOrderState };
  readonly setErrorMessage: (message: string) => void;
  readonly setIsDirty: (isDirty: boolean) => void;
  readonly saveCallback: MutableRefObject<() => Promise<void>>;
  readonly discardCallback: MutableRefObject<() => void>;
}): React.JSX.Element {
  const [isSaving, setIsSaving] = useState(false);
  const defaultTaxCostAdjustment = useMemo(() => ({ label: "Tax", purchase_order_id: purchaseOrder.id } as CostAdjustmentEditable), [purchaseOrder.id]);
  const [costAdjustments, setCostAdjustments] = useState<CostAdjustmentEditable[]>([defaultTaxCostAdjustment]);
  const { data: costAdjustmentsResponse, isLoading } = useCostAdjustments(purchaseOrder.id);
  const originalCostAdjustments = useRef<CostAdjustmentEditable[]>([]);

  useEffect(() => {
    const formattedCostAdjustments = sortCostAdjustments(formatClientSide(costAdjustmentsResponse.cost_adjustments));
    const taxCostAdjustment = formattedCostAdjustments.find((costAdjustment) => costAdjustment.label === "Tax");
    if (!taxCostAdjustment) {
      // prepend the default tax cost adjustment
      formattedCostAdjustments.unshift(defaultTaxCostAdjustment);
    }
    setCostAdjustments(cloneArrayOfObjects(formattedCostAdjustments));
    originalCostAdjustments.current = cloneArrayOfObjects(formattedCostAdjustments);
  }, [costAdjustmentsResponse, defaultTaxCostAdjustment]);

  const handleSaveCostAdjustments = useCallback(
    async (): Promise<void> => {
      setIsSaving(true);
      const payload = costAdjustments.map(
        (costAdjustment: CostAdjustmentEditable): CostAdjustment => ({
          ...costAdjustment,
          purchase_order_id: purchaseOrder.id,
          amount: formatAmount(costAdjustment),
        }),
      );
      try {
        const { cost_adjustments } = await saveCostAdjustments(purchaseOrder.id, payload);
        const formattedCostAdjustments = sortCostAdjustments(formatClientSide(cost_adjustments));
        setCostAdjustments(cloneArrayOfObjects(formattedCostAdjustments));
        originalCostAdjustments.current = cloneArrayOfObjects(formattedCostAdjustments);
      } catch (err) {
        Rollbar.error(err, { purchaseOrderId: purchaseOrder.id, payload });
        const message = extractMessageFromError(err);
        setErrorMessage(message);
      } finally {
        setIsSaving(false);
      }
    },
    [costAdjustments, purchaseOrder.id, setErrorMessage],
  );
  saveCallback.current = handleSaveCostAdjustments;

  const discard = useCallback(() => {
    setCostAdjustments(cloneArrayOfObjects(originalCostAdjustments.current));
  }, [originalCostAdjustments]);
  discardCallback.current = discard;

  const removeCostAdjustment = useCallback((index: number) => (): void => {
    const clonedCostAdjustments = costAdjustments.map((_item, i) => {
      if (i === index) {
        return {
          ..._item,
          _destroy: true,
        };
      }
      return _item;
    });
    setCostAdjustments(clonedCostAdjustments);
    setIsDirty(true);
  }, [costAdjustments, setIsDirty]);

  const handleCostLabelChange =
    useCallback((index: number) =>
    (label: string): void => {
      const newCostAdjustments = costAdjustments.map((costAdjustment, i) => {
        if (i === index) {
          return {
            ...costAdjustment,
            label,
          };
        }
        return costAdjustment;
      });
      setCostAdjustments(newCostAdjustments);
      setIsDirty(true);
    }, [costAdjustments, setIsDirty]);

  const handleCostAmountChange =
    useCallback((index: number) =>
    (amount: string): void => {
      const cleanAmount = amount?.replace(/[^\d.-]/g, "");
      const newCostAdjustments = costAdjustments.map((costAdjustment, i) => {
        if (i === index) {
          return {
            ...costAdjustment,
            amount: cleanAmount,
          };
        }
        return costAdjustment;
      });

      setCostAdjustments(newCostAdjustments);
      setIsDirty(true);
    }, [costAdjustments, setIsDirty]);

  const handleAddAdjustmentClick = useCallback((): void => {
    const clonedCostAdjustments: CostAdjustmentEditable[] = [
      ...costAdjustments,
      { label: "Other", purchase_order_id: purchaseOrder.id } as CostAdjustmentEditable,
    ];
    setCostAdjustments(clonedCostAdjustments);
    setIsDirty(true);
  }, [costAdjustments, purchaseOrder.id, setIsDirty]);

  const [taxCostAdjustment] = costAdjustments;
  const subTotal = assertNumber(lineTotal) + assertNumber(taxCostAdjustment.amount);
  
  const costAdjTotal = costAdjustments.reduce((acc, costAdj) => {
    if (costAdj._destroy) {
      return acc;
    }
    return acc + assertNumber(costAdj.amount);
  }, 0);
  const total = costAdjTotal + assertNumber(lineTotal);
  const receivedTotal = costAdjTotal + assertNumber(receivedCost);
  const minimumOrderValueWarning = total > 0 && total < (purchaseOrder?.supplier?.minimum_order_value || 0);

  const changeTaxAmount = handleCostAmountChange(0);

  let totalDisplay: string | React.ReactElement = formatMoney(total, currency, 4);
  const receivedTotalDisplay = formatMoney(receivedTotal, currency, 4);
  if (purchaseOrder.state !== ("draft" as PurchaseOrderState) && receivedTotal !== total) {
    totalDisplay = (
      <Tooltip content={`${receivedTotalDisplay} received of ${totalDisplay} total`}>
        <span>{`${receivedTotalDisplay} (${totalDisplay})`}</span>
      </Tooltip>
    );
  }

  return (
    <FormLayout>
      <FormLayout.Group>
        <LegacyStack alignment="center">
          <LegacyStack.Item fill>
            <Label id="taxesHeading">Taxes</Label>
          </LegacyStack.Item>
          <LegacyStack.Item>
            <div className="w120">
              <TextField
                align="right"
                autoComplete="off"
                id="tax"
                label=""
                labelHidden
                onChange={changeTaxAmount}
                placeholder="0.00"
                prefix={getCurrencySymbol(currency)}
                readOnly={isSaving || isLoading}
                type="currency"
                value={taxCostAdjustment.amount}
              />
            </div>
          </LegacyStack.Item>
          <LegacyStack.Item>
            <div style={{ width: "22px" }} />
          </LegacyStack.Item>
        </LegacyStack>
      </FormLayout.Group>
      <FormLayout.Group>
        <LegacyStack>
          <LegacyStack.Item fill>
            <Label id="subtotalHeading">Subtotal</Label>
          </LegacyStack.Item>
          <LegacyStack>
            <LegacyStack.Item>
              <TextContainer>{formatMoney(subTotal, currency, 4)}</TextContainer>
            </LegacyStack.Item>
            <LegacyStack.Item>
              <div style={{ width: "32px" }} />
            </LegacyStack.Item>
          </LegacyStack>
        </LegacyStack>
      </FormLayout.Group>
      {costAdjustments.map((costAdjustment, index) => (
        index !== 0 && !costAdjustment._destroy && (
          <FormLayout.Group key={costAdjustment.id}>
            <LegacyStack alignment="center" distribution="leading" spacing="tight">
              <LegacyStack.Item>
                <Select
                  disabled={isSaving || isLoading}
                  id="description"
                  label=""
                  labelHidden
                  onChange={handleCostLabelChange(index)}
                  options={COST_ITEMS.map((item) => ({ label: item, value: item, key: item }))}
                  value={costAdjustment.label}
                />
              </LegacyStack.Item>
              <LegacyStack.Item fill />
              <LegacyStack alignment="center" wrap={false}>
                <LegacyStack.Item>
                  <div className="w120">
                    <TextField
                      align="right"
                      autoComplete="off"
                      id="monetaryValue"
                      inputMode="numeric"
                      label=""
                      labelHidden
                      onChange={handleCostAmountChange(index)}
                      placeholder="0.00"
                      prefix={getCurrencySymbol(currency)}
                      readOnly={isSaving || isLoading}
                      type="currency"
                      value={costAdjustment.amount}
                    />
                  </div>
                </LegacyStack.Item>
                <LegacyStack.Item>
                  <Button
                    icon={<Icon source={XIcon} />}
                    key="3"
                    onClick={removeCostAdjustment(index)}
                    variant="plain"
                  />
                </LegacyStack.Item>
              </LegacyStack>
            </LegacyStack>
          </FormLayout.Group>
        )
      ))}
      <Button onClick={handleAddAdjustmentClick} variant="plain">
        Add adjustment
      </Button>
      <LegacyStack alignment="center" distribution="leading">
        <LegacyStack.Item>
          <Label id="totalHeading">Total</Label>
        </LegacyStack.Item>
        <LegacyStack.Item fill />
        <LegacyStack.Item>
          <TextContainer>{totalDisplay}</TextContainer>
        </LegacyStack.Item>
        <LegacyStack.Item>
          <div style={{ width: "32px" }} />
        </LegacyStack.Item>
      </LegacyStack>
      {minimumOrderValueWarning ? (
        <Tooltip content="Total is less than minimum order value">
          <Badge tone="attention">Total bill is less than minimum order value</Badge>
        </Tooltip>
      ) : null}
    </FormLayout>
  );
}

export default CostAdjustments;
